import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Calendar, Users2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Customer } from "@/types/customer-type";

type TopCustomersCardProps = {
  customers: Customer[];
  loading?: boolean;
  onView?: (id: string | number) => void;
  maxHeight?: string;
};

const currency = (n: number, cur = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(n);

export const TopCustomersCard: React.FC<TopCustomersCardProps> = ({
  customers,
  loading = false,
  onView,
  maxHeight = "400px",
}) => {
  if (!Array.isArray(customers)) {
    return (
      <Card className="h-full border-red-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Top Customers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-sm text-red-500 py-6">Error loading data.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <CardTitle className="text-base font-semibold">Top Customers</CardTitle>
          </div>
          {onView && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              onClick={() => onView("ALL")}
            >
              <Users2 className="h-3.5 w-3.5 mr-1" />
              View All
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-auto scrollbar-thin" style={{ maxHeight }}>
          <Table>
            <TableHeader className="bg-muted/50 sticky top-0 z-10">
              <TableRow>
                <TableHead className="w-[50px] md:w-[80px] text-[11px] uppercase">Rank</TableHead>
                <TableHead className="text-[11px] uppercase">Customer</TableHead>
                <TableHead className="hidden md:table-cell text-right text-[11px] uppercase">Orders</TableHead>
                <TableHead className="text-right text-[11px] uppercase">Total Spend</TableHead>
                <TableHead className="hidden sm:table-cell text-right text-[11px] uppercase">Last Seen</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground animate-pulse">
                    Loading top customers…
                  </TableCell>
                </TableRow>
              )}

              {!loading && customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    No customer data found.
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                customers.map((c, index) => (
                  <TableRow
                    key={c.id}
                    className="hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => onView?.(c.id)}
                  >
                    {/* Rank / ID column */}
                    <TableCell className="text-center font-mono text-[10px] text-muted-foreground">
                      {index + 1}
                    </TableCell>

                    {/* Customer Info */}
                    <TableCell className="py-3">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="relative shrink-0">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs md:text-sm font-bold overflow-hidden border-2 border-white shadow-sm">
                            {c?.profileImageUrl ? (
                              <img
                                src={c.profileImageUrl}
                                alt={c?.name || "avatar"}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              c?.name?.[0]?.toUpperCase() || "?"
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-gray-900 truncate">
                            {c?.name || "Guest Customer"}
                          </span>
                          <span className="md:hidden text-[10px] text-muted-foreground italic">
                            {c.totalOrder ?? 0} orders
                          </span>
                          <span className="hidden md:flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Calendar className="h-2.5 w-2.5" />
                            since {c.createdAt ? format(new Date(c.createdAt), "MMM yyyy") : "N/A"}
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    {/* Desktop Only Orders */}
                    <TableCell className="hidden md:table-cell text-right text-sm font-medium">
                      {c.totalOrder ?? 0}
                    </TableCell>

                    {/* Spend - Bolded for importance */}
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-gray-900">
                          {currency(c.totalSpend ?? 0)}
                        </span>
                        <span className="sm:hidden text-[10px] text-muted-foreground">
                          Total Spend
                        </span>
                      </div>
                    </TableCell>

                    {/* Last Order - Hidden on mobile */}
                    <TableCell className="hidden sm:table-cell text-right text-[11px] text-muted-foreground">
                      {c.lastOrder ? format(new Date(c.lastOrder), "MMM d, HH:mm") : "—"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};